import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"

import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Tooltip } from "@mui/material"
import CheckboxList from "components/Checkbox/CheckboxList"
import FileUploaderV2 from "components/FileUploaderV2"
import MuiAutoComplete from "components/MuiAutocomplete"
import MuiCheckbox from "components/MuiCheckbox"
import MuiTextField from "components/MuiTextField"
import { ProjectFormPageProps } from "components/ProjectForm"
import RadioGroup from "components/RadioGroup/RadioGroup"
import {
    AVAILABLE_CONNECTION_TYPE_OPTIONS,
    TYPE_OF_PROPERTY_OPTIONS,
    VEHICLE_TYPE_OPTIONS,
    YES_NO_OPTIONS,
} from "enums/dropdownValues"
import ProjectFormFooter from "../../components/ProjectFormFooter"
import useLandDeal, { ELandDealFields } from "./useLandDeal"

const LandDeal = (props: ProjectFormPageProps) => {
    const { states, functions } = useLandDeal(props)
    const { formik, isIncrementalYes } = states
    const selectedVehicleMovementCount = formik.values[ELandDealFields.VEHICLE_MOVEMENT].length
    const isAllVehicleMovementsSelected =
        VEHICLE_TYPE_OPTIONS.length > 0 && selectedVehicleMovementCount === VEHICLE_TYPE_OPTIONS.length

    return (
        <>
            <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="custom-scrollbar"
                maxHeight={"65vh"}
                padding={"var(--mui-modal-padding)"}
                sx={{
                    overflowY: "auto",
                }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                                <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                    Host Account
                                </Typography>
                                <Tooltip
                                    placement="top"
                                    slotProps={{
                                        tooltip: {
                                            sx: {
                                                maxWidth: "none",
                                            },
                                        },
                                    }}
                                    title="This field can only be updated from the 'Account and Site' page.">
                                    <FontAwesomeIcon icon={faCircleInfo} color="#A8A8A8" size="sm" />
                                </Tooltip>
                            </Box>
                            <MuiTextField
                                disabled
                                placeholder="Select Host Account"
                                size="large"
                                value={formik.values[ELandDealFields.HOST_ACCOUNT]?.title || ""}
                                error={functions.hasError(ELandDealFields.HOST_ACCOUNT)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Type of Property*
                            </Typography>
                            <RadioGroup
                                options={TYPE_OF_PROPERTY_OPTIONS}
                                name={ELandDealFields.PROPERTY_TYPE}
                                value={formik.values[ELandDealFields.PROPERTY_TYPE]}
                                onSelect={(value: any) => {
                                    formik.setFieldValue(ELandDealFields.PROPERTY_TYPE, value)
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid size={12}>
                        <Box display={"flex"} flexDirection={"column"} gap={1} sx={{ width: { xs: "100%", sm: "50%" } }}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Available connection type*
                            </Typography>
                            <RadioGroup
                                options={AVAILABLE_CONNECTION_TYPE_OPTIONS}
                                name={ELandDealFields.CONNECTION_TYPE}
                                value={formik.values[ELandDealFields.CONNECTION_TYPE]}
                                onSelect={(value: any) => {
                                    formik.setFieldValue(ELandDealFields.CONNECTION_TYPE, value)
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Total Load Available (in kWH)
                            </Typography>
                            <MuiTextField
                                placeholder="Enter Load"
                                size="large"
                                type="number"
                                value={formik.values[ELandDealFields.SANCTIONED_LOAD]}
                                onChange={e => formik.setFieldValue(ELandDealFields.SANCTIONED_LOAD, e.target.value)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Upload Bill*
                            </Typography>
                            <FileUploaderV2
                                key={`bill-${states.existingBillDocuments.length}-${states.project_id}`}
                                associatedId={states.project_id}
                                fileType={states.FILE_TYPES.UPLOAD_BILL}
                                onUpload={(fileInfos, prev) =>
                                    functions.handleFileUpload(fileInfos, states.FILE_TYPES.UPLOAD_BILL, prev)
                                }
                                maxFiles={1}
                                docIds={
                                    states.existingBillDocuments.length > 0
                                        ? states.existingBillDocuments.map(doc => doc.doc_id)
                                        : undefined
                                }
                                initialFileCount={states.existingBillDocuments.length}
                                existingFileNames={states.existingBillDocuments.map(doc => doc.document_title)}
                                error={functions.hasFileError(states.FILE_TYPES.UPLOAD_BILL)}
                            />
                        </Box>
                    </Grid>

                    <Grid size={formik.values[ELandDealFields.DG_CONNECTION_USED]?.id === 1 ? 6 : 12}>
                        <Box display={"flex"} flexDirection={"column"} gap={1} sx={{ width: { xs: "100%", sm: "50%" } }}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Is DG Connection Used?*
                            </Typography>
                            <RadioGroup
                                options={YES_NO_OPTIONS}
                                name={ELandDealFields.DG_CONNECTION_USED}
                                value={formik.values[ELandDealFields.DG_CONNECTION_USED]}
                                onSelect={(value: any) => {
                                    formik.setFieldValue(ELandDealFields.DG_CONNECTION_USED, value)
                                }}
                            />
                        </Box>
                    </Grid>
                    {formik.values[ELandDealFields.DG_CONNECTION_USED]?.id === 1 && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display={"flex"} flexDirection={"column"} gap={1}>
                                <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                    DG Price Per Unit*
                                </Typography>
                                <MuiTextField
                                    placeholder="Enter DG Price Per Unit"
                                    size="large"
                                    type="number"
                                    value={formik.values[ELandDealFields.DG_UNIT_RATE]}
                                    onChange={e => formik.setFieldValue(ELandDealFields.DG_UNIT_RATE, e.target.value)}
                                    error={functions.hasError(ELandDealFields.DG_UNIT_RATE)}
                                />
                            </Box>
                        </Grid>
                    )}

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Host Agreement*
                            </Typography>
                            <FileUploaderV2
                                key={`host-${states.existingHostAgreementDocuments.length}-${states.project_id}`}
                                associatedId={states.project_id}
                                fileType={states.FILE_TYPES.HOST_AGREEMENT}
                                onUpload={(fileInfos, prev) =>
                                    functions.handleFileUpload(fileInfos, states.FILE_TYPES.HOST_AGREEMENT, prev)
                                }
                                maxFiles={1}
                                docIds={
                                    states.existingHostAgreementDocuments.length > 0
                                        ? states.existingHostAgreementDocuments.map(doc => doc.doc_id)
                                        : undefined
                                }
                                initialFileCount={states.existingHostAgreementDocuments.length}
                                existingFileNames={states.existingHostAgreementDocuments.map(doc => doc.document_title)}
                                error={functions.hasFileError(states.FILE_TYPES.HOST_AGREEMENT)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                NOC*
                            </Typography>
                            <FileUploaderV2
                                key={`noc-${states.existingNocDocuments.length}-${states.project_id}`}
                                associatedId={states.project_id}
                                fileType={states.FILE_TYPES.NOC}
                                maxFiles={1}
                                docIds={
                                    states.existingNocDocuments.length > 0
                                        ? states.existingNocDocuments.map(doc => doc.doc_id)
                                        : undefined
                                }
                                onUpload={(fileInfos, prev) => functions.handleFileUpload(fileInfos, states.FILE_TYPES.NOC, prev)}
                                initialFileCount={states.existingNocDocuments.length}
                                existingFileNames={states.existingNocDocuments.map(doc => doc.document_title)}
                                error={functions.hasFileError(states.FILE_TYPES.NOC)}
                            />
                        </Box>
                    </Grid>

                    <Grid size={12}>
                        <Box display={"flex"} flexDirection={"column"} gap={1} sx={{ width: { xs: "100%", sm: "50%" } }}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Property Ownership
                            </Typography>
                            <FileUploaderV2
                                key={`prop-${states.existingPropertyOwnershipDocuments.length}-${states.project_id}`}
                                associatedId={states.project_id}
                                fileType={states.FILE_TYPES.PROPERTY_OWNERSHIP}
                                maxFiles={1}
                                docIds={
                                    states.existingPropertyOwnershipDocuments.length > 0
                                        ? states.existingPropertyOwnershipDocuments.map(doc => doc.doc_id)
                                        : undefined
                                }
                                onUpload={(fileInfos, prev) =>
                                    functions.handleFileUpload(fileInfos, states.FILE_TYPES.PROPERTY_OWNERSHIP, prev)
                                }
                                initialFileCount={states.existingPropertyOwnershipDocuments.length}
                                existingFileNames={states.existingPropertyOwnershipDocuments.map(
                                    doc => doc.document_title
                                )}
                            />
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Parking Slots
                            </Typography>
                            <MuiTextField
                                placeholder="No. Of Parking Spots"
                                size="large"
                                type="number"
                                value={formik.values[ELandDealFields.PARKING_SLOTS]}
                                onChange={e => formik.setFieldValue(ELandDealFields.PARKING_SLOTS, e.target.value)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Parking Type*
                            </Typography>
                            <MuiAutoComplete
                                disablePortal={false}
                                disableClearable
                                options={states.parkingTypeOptions}
                                placeholder="Select Parking Type"
                                error={functions.hasError(ELandDealFields.PARKING_TYPE)}
                                size="large"
                                value={formik.values[ELandDealFields.PARKING_TYPE]}
                                onChange={(value: any) => formik.setFieldValue(ELandDealFields.PARKING_TYPE, value)}
                            />
                        </Box>
                    </Grid>

                    {/* <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Deal Type
                            </Typography>
                            <RadioGroup
                                options={states.dealTypeOptions}
                                name={ELandDealFields.DEAL_TYPE}
                                value={formik.values[ELandDealFields.DEAL_TYPE]}
                                onSelect={(value: any) => {
                                    formik.setFieldValue(ELandDealFields.DEAL_TYPE, value)
                                }}
                            />
                        </Box>
                    </Grid> */}

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Fixed Rental Amount*
                            </Typography>
                            <MuiTextField
                                placeholder="Enter Rental Amount"
                                size="large"
                                type="number"
                                value={formik.values[ELandDealFields.DEAL_AMOUNT]}
                                onChange={e => formik.setFieldValue(ELandDealFields.DEAL_AMOUNT, e.target.value)}
                                error={functions.hasError(ELandDealFields.DEAL_AMOUNT)}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Incremental
                            </Typography>
                            <RadioGroup
                                options={YES_NO_OPTIONS}
                                name={ELandDealFields.INCREMENTAL}
                                value={formik.values[ELandDealFields.INCREMENTAL]}
                                onSelect={(value: any) => {
                                    formik.setFieldValue(ELandDealFields.INCREMENTAL, value)
                                }}
                            />
                        </Box>
                    </Grid>

                    {isIncrementalYes && (
                        <>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display={"flex"} flexDirection={"column"} gap={1}>
                                    <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                        Incremental %*
                                    </Typography>
                                    <MuiTextField
                                        placeholder="Enter Incremental %"
                                        size="large"
                                        type="number"
                                        value={formik.values[ELandDealFields.INCREMENTAL_PERCENT]}
                                        onChange={e =>
                                            formik.setFieldValue(ELandDealFields.INCREMENTAL_PERCENT, e.target.value)
                                        }
                                        error={functions.hasError(ELandDealFields.INCREMENTAL_PERCENT)}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box display={"flex"} flexDirection={"column"} gap={1}>
                                    <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                        Incremental Duration*
                                    </Typography>
                                    <MuiAutoComplete
                                        disablePortal={false}
                                        options={states.incrementalDurationOptions}
                                        placeholder="Select Incremental Duration"
                                        size="large"
                                        value={formik.values[ELandDealFields.INCREMENTAL_DURATION]}
                                        onChange={value =>
                                            formik.setFieldValue(ELandDealFields.INCREMENTAL_DURATION, value)
                                        }
                                        error={functions.hasError(ELandDealFields.INCREMENTAL_DURATION)}
                                    />
                                </Box>
                            </Grid>
                        </>
                    )}

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box display={"flex"} flexDirection={"column"} gap={1}>
                            <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                Land Size
                            </Typography>
                            <MuiTextField
                                placeholder="Enter Land Size"
                                size="large"
                                type="number"
                                value={formik.values[ELandDealFields.LAND_SIZE]}
                                onChange={e => formik.setFieldValue(ELandDealFields.LAND_SIZE, e.target.value)}
                            />
                        </Box>
                    </Grid>

                    {/* {isProfitShareDeal && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box display={"flex"} flexDirection={"column"} gap={1}>
                                <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                    Profit Share %*
                                </Typography>
                                <MuiTextField
                                    placeholder="Enter Profit Share %"
                                    size="large"
                                    type="number"
                                    value={formik.values[ELandDealFields.PROFIT_SHARE_PERCENT]}
                                    onChange={e =>
                                        formik.setFieldValue(ELandDealFields.PROFIT_SHARE_PERCENT, e.target.value)
                                    }
                                    error={functions.hasError(ELandDealFields.PROFIT_SHARE_PERCENT)}
                                />
                            </Box>
                        </Grid>
                    )} */}

                    <Grid size={12}>
                        <Box display={"flex"} flexDirection={"column"} gap={1} sx={{ width: { xs: "100%", sm: "50%" } }}>
                            <Box display="flex" alignItems="center" gap={1} justifyContent={"space-between"}>
                                <Typography fontSize={"14px"} variant="label" textTransform={"none"}>
                                    Select which vehicle movement is feasible
                                </Typography>
                                <Box>
                                    <MuiCheckbox
                                        checked={isAllVehicleMovementsSelected}
                                        sx={{
                                            marginRight: 0,
                                        }}
                                        onChange={event => {
                                            formik.setFieldValue(
                                                ELandDealFields.VEHICLE_MOVEMENT,
                                                event.target.checked ? VEHICLE_TYPE_OPTIONS : []
                                            )
                                        }}
                                    />
                                    <Typography fontSize={"12px"} variant="label" textTransform={"none"}>
                                        Select All
                                    </Typography>
                                </Box>
                            </Box>
                            <CheckboxList
                                name={ELandDealFields.VEHICLE_MOVEMENT}
                                options={VEHICLE_TYPE_OPTIONS}
                                onChange={value => {
                                    formik.setFieldValue(ELandDealFields.VEHICLE_MOVEMENT, value)
                                }}
                                initialValues={formik.values[ELandDealFields.VEHICLE_MOVEMENT]}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <ProjectFormFooter
                handleCloseForm={props.handleCloseForm}
                handleSubmit={() => formik.handleSubmit()}
                isLoading={states.isSubmitting}
            />
        </>
    )
}

export default LandDeal
